import { Inject, Injectable } from '@angular/core';
import { Experiment, ExperimentClient, ExperimentUser } from '@amplitude/experiment-js-client';
import { FeatureFlag, FeatureFlagProvider, FeatureFlagUser } from './feature-flag.interfaces';
import { AMPLITUDE_CONFIGURATION, AmplitudeConfiguration } from 'projects/sdk/packages/core/src/interfaces/amplitude-configuration';

@Injectable({ providedIn: 'root' })
export class AmplitudeFeatureFlagProvider implements FeatureFlagProvider {
  private experimentClient: ExperimentClient;

  constructor(@Inject(AMPLITUDE_CONFIGURATION) protected amplitudeConfiguration: AmplitudeConfiguration) {}

  async initialize(): Promise<void> {
    this.experimentClient = Experiment.initializeWithAmplitudeAnalytics(this.amplitudeConfiguration.amplitudeDeploymentKey);
  }

  async fetch(user: FeatureFlagUser): Promise<Record<string, FeatureFlag>> {
    const amplitudeUser: ExperimentUser = {
      user_id: user.userId,
      ...user.properties
    };

    // The fetch() method never fails, it only returns flags last recorded to Amplitude's SDK storage.
    await this.experimentClient.fetch(amplitudeUser);
    const variants = this.experimentClient.all();
    return this.transformResponse(variants);
  }

  transformResponse(response: any): Readonly<Record<string, Readonly<FeatureFlag>>> {
    const flags: Record<string, Readonly<FeatureFlag>> = {};

    Object.keys(response).forEach(key => {
      const amplitudeFlag = response[key];
      flags[key] = Object.freeze({
        code: key,
        key: amplitudeFlag.key || key,
        value: amplitudeFlag.value,
        payload: amplitudeFlag.payload,
        metadata: amplitudeFlag.metadata
      });
    });

    return Object.freeze(flags);
  }

  trackExposure(flagCode: string): void {
    this.experimentClient.exposure(flagCode);
  }
}
